import React, {useCallback, useState} from "react";
import LoadingIndicator from "../LoadingIndicator";
import MobileUserAccountDetails from "./MobileUserAccountDetails";
import MobileUserAccountWallet from "./MobileUserAccountWallet";
import {Button, Paper, Typography} from "@material-ui/core";
import {AccountStatusOptions} from "../../models/mobileUserAccounts/MobileUserAccountsListModel";
import ConfirmationButton from "../common/ConfirmationButton";
import {useTranslation} from "react-i18next";
import {useDispatch, useSelector} from "react-redux";
import {actions} from '../../actions/mobileUserAccountsActions';

const MobileUserAccountDetailsInfo = () => {

  const dispatch = useDispatch();

  const [actionInProgress, setActionInProgress] = useState(false);

  const {t} = useTranslation();

  const {fetching, data: account} = useSelector(state => state.mobileUserAccountDetails)


  const handleUnblockVerification = useCallback(() => {
    setActionInProgress(true);
    dispatch(actions.unblockVerification(account.userId))
      .finally(() => setActionInProgress(false))
  }, [account, dispatch]);

  const handleChangeTesterRole = useCallback(() => {
    setActionInProgress(true);
    const action = account.isTester
      ? actions.revokeTesterRole(account.userId)
      : actions.grantTesterRole(account.userId);
    dispatch(action).finally(() => setActionInProgress(false));
  }, [account, dispatch]);

  const handleChangeDeveloperRole = useCallback(() => {
    setActionInProgress(true);
    const action = account.isDeveloper
      ? actions.revokeDeveloperRole(account.userId)
      : actions.grantDeveloperRole(account.userId);
    dispatch(action).finally(() => setActionInProgress(false));
  }, [account, dispatch]);

  const handleWithdraw = useCallback(() => {
    setActionInProgress(true);
    dispatch(actions.withdraw(account.userId))
      .finally(() => setActionInProgress(false));
  }, [account, dispatch]);

  const handleFundsReturn = useCallback(() => {
    setActionInProgress(true);
    dispatch(actions.registerAllFundsReturn(account.userId))
      .finally(() => setActionInProgress(false));
  }, [account, dispatch]);

  const handleDelete = useCallback(() => {
    setActionInProgress(true);
    dispatch(actions.delete(account.userId))
      .finally(() => setActionInProgress(false));
  }, [account, dispatch]);

  const handleLock = useCallback(() => {
    setActionInProgress(true);
    dispatch(actions.lock(account.userId))
      .finally(() => setActionInProgress(false));
  }, [account, dispatch]);

  const handleUnlock = useCallback(() => {
    setActionInProgress(true);
    dispatch(actions.unlock(account.userId))
      .finally(() => setActionInProgress(false));
  }, [account, dispatch]);


  return (
    <div style={{padding: 16}}>
      <LoadingIndicator fetching={fetching}>
        {account &&
          <React.Fragment>
            <MobileUserAccountDetails account={account}/>
            <div style={{marginTop: 16}}>
              <MobileUserAccountWallet account={account} onWithdraw={handleWithdraw}
                                       onFundsReturn={handleFundsReturn} disabled={actionInProgress}/>
            </div>

            {account.verificationBlocked && <Paper style={{marginTop: 16, padding: 16}} elevation={0}>
              <Typography gutterBottom variant="body1"
                          color="error">{t('mobileUserAccounts.details.verificationBlocked')}</Typography>
              <Button size="small" variant="outlined" color="secondary" onClick={handleUnblockVerification}
                      disabled={!account.verificationBlocked || account.status === AccountStatusOptions.deleted.value || actionInProgress}>
                {t('mobileUserAccounts.details.unblockButton')}
              </Button>
            </Paper>}

            {account.status === AccountStatusOptions.deleted.value &&
              <Paper style={{marginTop: 16, padding: 16}} elevation={0}>
                <Typography gutterBottom variant="body1"
                            color="error">{t('mobileUserAccounts.details.accountDeleted')}</Typography>
                <ul>
                  <li>
                    <Typography variant="body2">
                      <a href="https://app.onesignal.com/apps/7223d107-fc8d-488d-98d4-cf26648c1b44/segments"
                         target="_blank" rel="noopener noreferrer">Przejdź do OneSignal</a>
                    </Typography>
                  </li>
                  <li>
                    <Typography variant="body2">
                      Wybierz segment <strong>PO ID</strong>
                    </Typography>
                  </li>
                  <li>
                    <Typography variant="body2">
                      Wprowadź identyfikator <strong>{account.userId}</strong> i zapisz segment
                    </Typography>
                  </li>
                  <li>
                    <Typography variant="body2">
                      <a
                        href="https://dashboard.onesignal.com/apps/7223d107-fc8d-488d-98d4-cf26648c1b44/players?segment=cb88650c-8386-4ba7-ba3e-56d64319cc44"
                        target="_blank" rel="noopener noreferrer">Użyj segmentu</a> aby odnaleźć i usunąć użytkownika
                    </Typography>
                  </li>
                </ul>
              </Paper>}

            <Paper style={{marginTop: 16, padding: 16}} elevation={0}>
              <Button size="small" variant="outlined" onClick={handleChangeTesterRole} style={{marginRight: 10}}
                      disabled={account.status === AccountStatusOptions.deleted.value || actionInProgress}>
                {account.isTester ? t('mobileUserAccounts.details.revokeTesterRoleButton') : t('mobileUserAccounts.details.grantTesterRoleButton')}
              </Button>
              <Button size="small" variant="outlined" onClick={handleChangeDeveloperRole} style={{marginRight: 10}}
                      disabled={account.status === AccountStatusOptions.deleted.value || actionInProgress}>
                {account.isDeveloper ? t('mobileUserAccounts.details.revokeDeveloperRoleButton') : t('mobileUserAccounts.details.grantDeveloperRoleButton')}
              </Button>
              {account.status === AccountStatusOptions.locked.value &&
                <Button size="small" variant="outlined" onClick={handleUnlock} style={{marginRight: 10}}
                        disabled={account.status === AccountStatusOptions.deleted.value || actionInProgress}>
                  {t('mobileUserAccounts.details.unlockAccountButton')}
                </Button>}
              {account.status === AccountStatusOptions.active.value && <ConfirmationButton
                size="small"
                variant="outlined"
                onConfirm={handleLock}
                style={{marginRight: 10}}
                disabled={account.status === AccountStatusOptions.deleted.value || actionInProgress}
                confirmationTitle={t('mobileUserAccounts.details.lockAccountConfirmationTitle')}
                confirmationText={t('mobileUserAccounts.details.lockAccountConfirmationText')}
                confirmLabel={t('mobileUserAccounts.details.lockAccountButton')}
              >
                {t('mobileUserAccounts.details.lockAccountButton')}
              </ConfirmationButton>}
              <ConfirmationButton
                size="small"
                variant="contained"
                onConfirm={handleDelete}
                style={{marginRight: 10}}
                disabled={account.status === AccountStatusOptions.deleted.value || actionInProgress}
                confirmationTitle={t('mobileUserAccounts.details.deleteConfirmationTitle')}
                confirmationText={t('mobileUserAccounts.details.deleteConfirmationText')}
                confirmLabel={t('mobileUserAccounts.details.deleteButton')}
              >
                {t('mobileUserAccounts.details.deleteButton')}
              </ConfirmationButton>
            </Paper>
          </React.Fragment>
        }
      </LoadingIndicator>
    </div>
  )

}

export default MobileUserAccountDetailsInfo
